import type { QueryKey } from '@tanstack/react-query';
import { queryClient } from './query-client.js';

export interface LookupOption {
  value: string;
  label: string;
}

export interface Lookup {
  /** A typed search, as a relation picker asks it. */
  search: (query: string) => Promise<LookupOption[]>;
  /** The label for an id already chosen — a restored filter, a form opened on a record. */
  resolve: (id: string) => Promise<LookupOption | null>;
}

export interface LookupSpec<T> {
  /**
   * Where the answers are cached: under the resource's own key, so creating or
   * renaming a record (which invalidates that key) refreshes its pickers too.
   */
  key: QueryKey;
  search: (query: string, signal: AbortSignal) => Promise<T[]>;
  get: (id: string, signal: AbortSignal) => Promise<T>;
  toOption: (item: T) => LookupOption;
  /** How long an answer is reused. Reference data that never changes can say `Infinity`. */
  staleTime?: number;
}

/**
 * A relation picker's search and resolve, through the query cache.
 *
 * The pickers used to call the API directly: every filter bar that restored a
 * user asked for a hundred users on every mount, and the same label was
 * fetched once per field that showed it. Through `fetchQuery` the same
 * question within `staleTime` is answered from memory, and two fields asking
 * at once share one request.
 */
export function makeLookup<T>(spec: LookupSpec<T>): Lookup {
  const staleTime = spec.staleTime ?? 60_000;

  return {
    search: async (query) => {
      const items = await queryClient.fetchQuery({
        queryKey: [...spec.key, 'lookup', 'search', query],
        queryFn: ({ signal }) => spec.search(query, signal),
        staleTime,
      });
      return items.map(spec.toOption);
    },
    resolve: async (id) => {
      try {
        const item = await queryClient.fetchQuery({
          queryKey: [...spec.key, 'lookup', 'item', id],
          queryFn: ({ signal }) => spec.get(id, signal),
          staleTime,
          // A reference to a record the user cannot see — or one deleted since —
          // reads as unknown, and asking again will not change that.
          retry: false,
        });
        return spec.toOption(item);
      } catch {
        return null;
      }
    },
  };
}
